import React, {
  useCallback,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import {
  IndicatorDetailSectionProps,
  IndicatorDetails,
  fileUpload,
} from '../utils';
import NextIndicator from './NextIndicator';
import { IndicatorDetailsHandler } from '../../../../NPOFeedback/FeedbackDetails/ImpactDetailsEdit/utils';
import {
  getIndicatorAssumptionQuestionText,
  getIndicatorDetailsMeasurement,
} from './utils';
import { IndicatorSectionKey } from '../../ImpactIndicator/utils';
import IndicatorFileUploadRender, {
  getIndicatorFileValue,
  indicatorFileDescriptionInitialState,
  indicatorFileUploadInitialState,
  saveIndicatorFileValue,
  updateIndicatorFileValue,
} from './IndicatorFileUploadRender';
import { useIndicator } from 'shared/providers/IndicatorProvider';

export const isDirectServiceOrEstimate = (
  selectedSectionKey: IndicatorSectionKey,
  indicatorDetails: IndicatorDetails,
) => {
  return (
    getIndicatorDetailsMeasurement(selectedSectionKey, 'Guess').findIndex(
      x => x === indicatorDetails.measurement,
    ) === -1
  );
};

export const isDirectServiceOrEstimateSelectionNotValid = (
  sectionKey: IndicatorSectionKey,
  indicatorDetails: IndicatorDetails,
) => {
  const description = indicatorFileDescriptionInitialState(indicatorDetails);
  return (
    !description && isDirectServiceOrEstimate(sectionKey, indicatorDetails)
  );
};

const StyledDiv = styled.div`
  .file-heading {
    max-width: 574px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  .file-subheading {
    max-width: 670px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${SPACERS.large};
  }
  textarea {
    border-radius: 4px;
    border: 1px solid transparent;
  }
  .indicator-upload-file-container.required {
    textarea {
      border-color: ${COLORS.critical};
    }
  }
`;

const IndicatorFileUpload = React.forwardRef<
  IndicatorDetailsHandler,
  IndicatorDetailSectionProps & {
    indicatorDetails: IndicatorDetails;
  }
>(
  (
    {
      hideBottomButtons,
      indicators,
      requestState,
      indicatorDetails,
      selectedIndex,
      selectedSectionKey,
      reportId,
      className,
      saveRequestState,
      setSelectedIndex,
      setSelectedSectionKey,
      setRoute,
      setIndicators,
      handleIndicatorSave,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const { getSourcesAndAssumptions } = useIndicator();
    const [open, setOpen] = useState<boolean>(false);
    const [files, setFiles] = useState<TrueImpactFile[]>(
      indicatorFileUploadInitialState(indicatorDetails),
    );
    const [description, setDescription] = useState<string>(
      getSourcesAndAssumptions(indicatorDetails),
    );

    useEffect(() => {
      setFiles(indicatorFileUploadInitialState(indicatorDetails));
      setDescription(getSourcesAndAssumptions(indicatorDetails));
    }, [indicatorDetails]);

    useEffect(() => {
      if (saveRequestState.type === 'REQUEST_SUCCESS') {
        setOpen(true);
      }
    }, [saveRequestState]);

    useImperativeHandle(forwardRef, () => ({
      answer: () => ({
        answer: saveIndicatorFileValue(files, description),
        errorText: undefined,
      }),
    }));

    const handleIndicatorValues = useCallback(() => {
      setIndicators(
        updateIndicatorFileValue(
          files,
          description,
          selectedIndex,
          selectedSectionKey,
        ),
      );
      const newIndicatorDetails = { ...indicatorDetails };
      newIndicatorDetails.file = getIndicatorFileValue(files, description);
      handleIndicatorSave(newIndicatorDetails);
    }, [
      files,
      description,
      selectedIndex,
      indicatorDetails,
      selectedSectionKey,
      handleIndicatorSave,
      setIndicators,
    ]);

    const handleNextIndicator = useCallback(() => {
      // setOpen(true);
      handleIndicatorValues();
    }, [handleIndicatorValues]);

    const text = getIndicatorAssumptionQuestionText(
      selectedSectionKey,
      indicatorDetails.measurement,
    );

    const isDirectServiceOrEstimateSelectionNotValid =
      !description &&
      isDirectServiceOrEstimate(selectedSectionKey, indicatorDetails);

    if (open) {
      return (
        <NextIndicator
          indicators={indicators}
          selectedIndex={selectedIndex}
          selectedSectionKey={selectedSectionKey}
          totalIndicators={indicators[selectedSectionKey].length}
          setRoute={setRoute}
          setSelectedIndex={setSelectedIndex}
          setSelectedSectionKey={setSelectedSectionKey}
        />
      );
    }

    return (
      <StyledDiv className={className}>
        <TrueImpactText variant="heading-page" className="file-heading">
          {text.heading}
        </TrueImpactText>
        {text.subheading && (
          <TrueImpactText className="file-subheading">
            <strong>For example:</strong> {text.subheading}
          </TrueImpactText>
        )}
        <IndicatorFileUploadRender
          files={files}
          reportId={reportId}
          setFiles={setFiles}
          description={description}
          setDescription={setDescription}
          className={'indicator-upload-file-container'}
        />
        {!hideBottomButtons && (
          <IndicatorBottomButtons
            requestState={requestState}
            disabledNext={isDirectServiceOrEstimateSelectionNotValid}
            nextButtonText="Complete Indicator"
            onNext={handleNextIndicator}
            onPrevious={onPrevious}
          />
        )}
      </StyledDiv>
    );
  },
);
export const indicatorFileUpload = async ({
  file,
  reportId,
  setFile,
}: {
  file: File | undefined;
  reportId: string | number;
  setFile: React.Dispatch<React.SetStateAction<TrueImpactFile | undefined>>;
}) => {
  if (!file) {
    setFile(s => ({
      ...s,
      error: undefined,
      file: undefined,
      fileName: undefined,
    }));
    return;
  } else {
    setFile(s => ({
      ...s,
      error: undefined,
      isUploading: true,
    }));
  }
  const uploadResult = await fileUpload(reportId, file);
  if (uploadResult.success) {
    setFile(s => ({
      ...s,
      ...uploadResult,
      file,
      fileName: file.name,
      isUploading: false,
      error: undefined,
    }));
  } else {
    setFile(s => ({
      ...s,
      file: undefined,
      fileName: undefined,
      isUploading: false,
      error:
        uploadResult.error?.message || 'Something went wrong while uploading.',
    }));
  }
};

export default IndicatorFileUpload;
